// BlogPostForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import apiClient from '../../api/AxiosConfig';
import BlogEditor from './BlogEditor'; // Import the separate editor component

const BlogPostForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    readTime: '5 min read',
    slug: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);
  
  // Categories for dropdown
  const categories = [
    'Development',
    'Design',
    'Technology',
    'Tutorials',
    'Other'
  ];
  
  // Fetch blog post data if editing
  useEffect(() => {
    const fetchBlogPost = async () => {
      if (isEditing) {
        try {
          setIsLoading(true);
          const res = await apiClient.get(`/blog/${id}`);
          const post = res.data.data;
          
          setFormData({
            title: post.title || '',
            excerpt: post.excerpt || '',
            content: post.content || '',
            category: post.category || '',
            readTime: post.readTime || '5 min read',
            slug: post.slug || ''
          });
          
          // If we already have a slug, disable auto-generation
          if (post.slug) {
            setAutoGenerateSlug(false);
          }
        } catch (err) {
          setError('Failed to load blog post');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchBlogPost();
  }, [id, isEditing]);
  
  // Generate slug from title
  useEffect(() => {
    if (autoGenerateSlug && formData.title) {
      const generatedSlug = formData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim(); // Trim whitespace
      
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  }, [formData.title, autoGenerateSlug]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSlugToggle = () => {
    setAutoGenerateSlug(!autoGenerateSlug);
    
    // If turning on auto-generate, regenerate slug from current title
    if (!autoGenerateSlug && formData.title) {
      const generatedSlug = formData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  };
  
  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    // Always ensure slug exists before submission
    let submissionData = {...formData};
    
    // Validate slug exists - this is critical!
    if (!submissionData.slug || submissionData.slug.trim() === '') {
      // Generate from title as fallback
      if (submissionData.title) {
        submissionData.slug = submissionData.title
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
      } else {
        setError('A title or slug is required');
        setIsLoading(false);
        return;
      }
    }
    
    console.log('Submitting form data:', submissionData);
    
    try {
      if (isEditing) {
        const response = await apiClient.put(`/blog/${id}`, submissionData);
        console.log('Update response:', response.data);
        setSuccess('Blog post updated successfully');
      } else {
        const response = await apiClient.post('/blog', submissionData);
        console.log('Create response:', response.data);
        setSuccess('Blog post created successfully');
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          category: '',
          readTime: '5 min read',
          slug: ''
        });
      }
      
      // Redirect after short delay to show success message
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error submitting form:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to save blog post');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/admin/dashboard')}
                className="mr-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-slate-800 dark:text-white">
                {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h1>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all flex items-center disabled:opacity-70"
            >
              <Save className="h-5 w-5 mr-2" />
              {isLoading ? 'Saving...' : 'Save Post'}
            </motion.button>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        {/* Alerts */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-start"
          >
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </motion.div>
        )}
        
        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg"
          >
            {success}
          </motion.div>
        )}
        
        {/* Form */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Title
              </label>
              <input
                id="title"
                name="title"
                type="text"
                required
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                placeholder="Enter post title"
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="slug" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Slug
                </label>
                <div className="flex items-center">
                  <input
                    id="autoSlug"
                    type="checkbox"
                    checked={autoGenerateSlug}
                    onChange={handleSlugToggle}
                    className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4 mr-2"
                  />
                  <label htmlFor="autoSlug" className="text-xs text-slate-600 dark:text-slate-400">
                    Auto-generate from title
                  </label>
                </div>
              </div>
              <input
                id="slug"
                name="slug"
                type="text"
                required
                value={formData.slug || ''}
                onChange={handleChange}
                disabled={autoGenerateSlug}
                className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all ${
                  autoGenerateSlug ? 'opacity-70' : ''
                }`}
                placeholder="post-url-slug"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                This will be used for the URL: yourblog.com/blog/post-slug
              </p>
            </div>
            
            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Excerpt
              </label>
              <input
                id="excerpt"
                name="excerpt"
                type="text"
                required
                value={formData.excerpt}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                placeholder="Brief summary of the post"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="readTime" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Read Time
                </label>
                <input
                  id="readTime"
                  name="readTime"
                  type="text"
                  value={formData.readTime}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                  placeholder="e.g. 5 min read"
                />
              </div>
            </div>
            
            {/* Using the separate BlogEditor component */}
            <BlogEditor 
              content={formData.content} 
              onChange={handleContentChange} 
            />
            
          </form>
        </div>
      </main>
    </div>
  );
};

export default BlogPostForm;              